import React, { useEffect, useState } from 'react';
import 'react-reflex/styles.css';
import { alert, AlertType, LoadingView } from '@quyva/shared-ui';
import { camelToTitleCase } from '@quyva/common';
import { AuthenticationForm } from './authenticationForm';
import { BotCreationForm } from './botCreationForm';
import { GroupCreationForm } from './groupCreationForm';
import { FeatureEnabledView } from './featureEnabledView';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleCheck,
    faRobot,
    faUser,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { TelegramSocketEvent } from './enums';
import { ErrorPage } from './errorPage';
import { countryCodes } from './countryCodes';

export const formStatus = {
    authentication: {
        description: 'Authenticate telegram account',
        icon: faUser,
    },
    botCreation: { description: 'Create telegram bot', icon: faRobot },
    groupCreation: {
        description: 'Create telegram group',
        icon: faUsers,
    },
    telegramBotAndGroupEnabled: {
        description: 'Enjoy telegram Integration with repository',
        icon: faCircleCheck,
    },
};

const statusKeys = Object.keys(formStatus);

const isValidStatus = (status) => {
    return status >= 0 && status <= 3;
};

const FormViewHeader = ({ status, title }) => {
    return (
        <div className='w-full flex items-center justify-center font-semibold text-lg mb-4'>
            <FontAwesomeIcon
                icon={formStatus[statusKeys[status]].icon}
                className={
                    status === 3
                        ? 'text-icons-success'
                        : 'text-texts-secondary300'
                }
            />
            <div className='text-texts-secondary300 ml-2'>{title}</div>
        </div>
    );
};

const FormViewBody = ({
    user,
    socket,
    botName,
    setBotName,
    status,
    repository,
    countryCode,
    setCountryCode,
    phoneNumber,
    setPhoneNumber,
    enableOTP,
    groupName,
    setGroupName,
    groupUrl,
    setShowTelegramForm,
    setIsLoading,
    setLoadingText,
    onRefreshVirtualRepos,
}) => {
    if (status === 0) {
        return (
            <AuthenticationForm
                user={user}
                socket={socket}
                repository={repository}
                countryCode={countryCode}
                setCountryCode={setCountryCode}
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
                enableOTP={enableOTP}
                setShowTelegramForm={setShowTelegramForm}
                setIsLoading={setIsLoading}
                setLoadingText={setLoadingText}
            />
        );
    } else if (status === 1) {
        return (
            <BotCreationForm
                user={user}
                socket={socket}
                botName={botName}
                setBotName={setBotName}
                repository={repository}
                setShowTelegramForm={setShowTelegramForm}
                setIsLoading={setIsLoading}
                setLoadingText={setLoadingText}
                onRefreshVirtualRepos={onRefreshVirtualRepos}
            />
        );
    } else if (status === 2) {
        return (
            <GroupCreationForm
                user={user}
                socket={socket}
                botName={botName}
                repository={repository}
                setShowTelegramForm={setShowTelegramForm}
                setIsLoading={setIsLoading}
                setLoadingText={setLoadingText}
                groupName={groupName}
                setGroupName={setGroupName}
                onRefreshVirtualRepos={onRefreshVirtualRepos}
            />
        );
    } else if (status === 3) {
        return (
            <FeatureEnabledView
                socket={socket}
                botName={botName}
                repository={repository}
                groupName={groupName}
                groupUrl={groupUrl}
                setShowTelegramForm={setShowTelegramForm}
                onRefreshVirtualRepos={onRefreshVirtualRepos}
            />
        );
    } else {
        return <ErrorPage />;
    }
};

export const TelegramFormView = ({
    user,
    repository,
    socket,
    setShowTelegramForm,
    getAllReposData,
    onRefreshVirtualRepos,
}) => {
    const [status, setStatus] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Loading...');
    const [countryCode, setCountryCode] = useState(countryCodes[0]);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [enableOTP, setEnableOTP] = useState(false);
    const [botName, setBotName] = useState('');
    const [groupName, setGroupName] = useState('');
    const [groupUrl, setGroupUrl] = useState('');

    useEffect(() => {
        setLoadingText('Loading progress...');
        setIsLoading(true);

        const data = {
            userMail: user.email,
            repoName: repository.name,
            repoId: repository.repoid,
        };

        const handleStatusUpdate = async (data) => {
            setIsLoading(false);
            setLoadingText('Loading...');

            if (data.progress !== undefined || data.progress !== null) {
                setStatus(data.progress);

                if (data.progress === 0 && data.enableOTP) {
                    setEnableOTP(true);
                }

                if (data.progress === 2) {
                    if (data.botName) setBotName(data.botName);
                    await getAllReposData();
                }

                if (data.progress === 3) {
                    if (data.botName) setBotName(data.botName);
                    if (data.groupName) setGroupName(data.groupName);
                    if (data.groupUrl) setGroupUrl(data.groupUrl);
                }
            }
        };

        const handleGetOtp = (data) => {
            console.log(`Get OTP for +${data.countryCode}${data.phoneNumber}`);
            setEnableOTP(true);
            setIsLoading(false);
            setLoadingText('Loading...');
        };

        const handleFormError = (data) => {
            setIsLoading(false);
            setLoadingText('Loading...');

            if (data.error) {
                alert(AlertType.ERROR, data.error);
            }
        };

        socket.on(
            TelegramSocketEvent.TELEGRAM_FORM_STATUS_UPDATE,
            handleStatusUpdate
        );
        socket.on(TelegramSocketEvent.GET_OTP, handleGetOtp);
        socket.on(TelegramSocketEvent.TELEGRAM_FORM_ERROR, handleFormError);

        socket.emit(TelegramSocketEvent.TELEGRAM_FORM_STATUS, data);

        return () => {
            socket.off(
                TelegramSocketEvent.TELEGRAM_FORM_STATUS_UPDATE,
                handleStatusUpdate
            );
            socket.off(TelegramSocketEvent.GET_OTP, handleGetOtp);
            socket.off(
                TelegramSocketEvent.TELEGRAM_FORM_ERROR,
                handleFormError
            );
        };
    }, [socket, user.email]);

    return (
        <div className='w-[700px] min-h-[320px] flex-col p-4'>
            <div className='w-full p-4'>
                {isLoading ? (
                    <div className='w-full h-full min-h-[250px] flex justify-center items-center w-full h-full p-4'>
                        <LoadingView loadingText={loadingText} />
                    </div>
                ) : (
                    <>
                        {isValidStatus(status) && (
                            <FormViewHeader
                                status={status}
                                title={camelToTitleCase(statusKeys[status])}
                            />
                        )}
                        <br />

                        <div className='flex items-center mb-6'>
                            <div className='w-1/2 font-semibold text-texts-secondary300'>
                                <label htmlFor='repository'>Repository</label>
                            </div>

                            <div className='w-full'>
                                <div className='relative flex items-center'>
                                    <div>{repository.name}</div>
                                </div>
                            </div>
                        </div>

                        <FormViewBody
                            user={user}
                            repository={repository}
                            socket={socket}
                            botName={botName}
                            setBotName={setBotName}
                            status={status}
                            countryCode={countryCode}
                            setCountryCode={setCountryCode}
                            phoneNumber={phoneNumber}
                            setPhoneNumber={setPhoneNumber}
                            enableOTP={enableOTP}
                            groupName={groupName}
                            setGroupName={setGroupName}
                            groupUrl={groupUrl}
                            setShowTelegramForm={setShowTelegramForm}
                            setIsLoading={setIsLoading}
                            setLoadingText={setLoadingText}
                            onRefreshVirtualRepos={onRefreshVirtualRepos}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
